import type { ScreenerReq } from '@revelio/data-access';
import { SelectionCategories, type TreeItem } from '@revelio/iso-utility';

import type { Filter } from '../../../filters/types/filters';
import {
  FilterType,
  isMultiFilter,
  isSegmentedFilter,
} from '../../../filters/types/filters';
import { getNumericIdFromNestedId } from '../../../filters/utils/get-numeric-id-from-nested-id';

/**
 * Adapts Custom Dash filter state to old screener format for deserialiseScreenerData
 * This allows the old screener's column naming logic to work with Custom Dash filters
 */
const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const getSegmentIds = (segmentValue: unknown): number[] => {
  if (!Array.isArray(segmentValue)) return [];
  return segmentValue.filter(isFiniteNumber);
};

const isTreeLevelMatch = (segmentKey: string, levelId?: string): boolean => {
  if (!levelId) return false;
  if (segmentKey === 'metro_area') {
    return levelId === 'msa' || levelId === 'metro_area';
  }
  if (segmentKey === 'job_category') {
    return levelId === 'role_k7' || levelId === 'job_category';
  }
  return levelId === segmentKey;
};

type SegmentLabel = {
  label: string;
  id: string;
};

const getTreeSegmentLabels = (
  segmentKey: string,
  segmentValueIds: number[],
  filter: Filter
): SegmentLabel[] => {
  if (filter.type !== FilterType.TREE) return [];

  return filter.selections
    .map((selection) => {
      const numericId = getNumericIdFromNestedId(selection.id);
      if (numericId === null) return null;
      if (!segmentValueIds.includes(numericId)) return null;
      if (!isTreeLevelMatch(segmentKey, selection.levelId)) return null;
      if (!selection.levelId) return null;
      if (!selection.name) return null;
      return {
        label: selection.name,
        id: String(selection.id),
      };
    })
    .filter((item): item is SegmentLabel => Boolean(item));
};

const getListSegmentLabels = (
  segmentKey: string,
  segmentValueIds: number[],
  filter: Filter
): SegmentLabel[] => {
  const isList =
    filter.type === FilterType.LIST || filter.type === FilterType.STATIC_LIST;
  if (!isList) return [];
  if (
    filter.type === FilterType.LIST &&
    filter.selectionListId !== segmentKey
  ) {
    return [];
  }

  return filter.selections
    .map((selection) => {
      const numericId = getNumericIdFromNestedId(String(selection.id));
      if (numericId === null) return null;
      if (!segmentValueIds.includes(numericId)) return null;
      if (!selection.name) return null;
      return {
        label: selection.name,
        id: String(selection.id),
      };
    })
    .filter((item): item is SegmentLabel => Boolean(item));
};

const buildTreeItem = (segment: SegmentLabel): TreeItem => ({
  item: { id: segment.id, shortName: segment.label },
  children: [],
  id: segment.id,
  selectionListId: SelectionCategories.COMPANY,
});

const buildTreeItems = (
  segmentFilters: Filter[],
  segmentFilterEntries: Array<[string, unknown]>
): Record<string, TreeItem> => {
  const treeItems: Record<string, TreeItem> = {};
  const labels: SegmentLabel[] = [];

  segmentFilterEntries.forEach(([segmentKey, segmentValue]) => {
    const segmentValueIds = getSegmentIds(segmentValue);
    if (segmentValueIds.length === 0) return;

    for (const filter of segmentFilters) {
      const treeLabels = getTreeSegmentLabels(
        segmentKey,
        segmentValueIds,
        filter
      );
      if (treeLabels.length > 0) {
        labels.push(...treeLabels);
        return;
      }

      const listLabels = getListSegmentLabels(
        segmentKey,
        segmentValueIds,
        filter
      );
      if (listLabels.length > 0) {
        labels.push(...listLabels);
        return;
      }
    }
  });

  labels.forEach((label, index) => {
    treeItems[`${index}`] = buildTreeItem(label);
  });

  return treeItems;
};

export const adaptFilterStateForScreener = (
  filters: Filter[],
  screenerFilters: Omit<ScreenerReq, 'pagination'>
) => {
  const flattenedFilters = filters.flatMap((filter) =>
    isMultiFilter(filter) ? filter.instances : [filter]
  );

  const segmentedFilters = flattenedFilters.filter(isSegmentedFilter);

  const segmentFilters = segmentedFilters.flatMap((filter) =>
    Object.values(filter.segmentations || {}).filter((value): value is Filter =>
      Boolean(value)
    )
  );

  // Now build the sub_filters array to match the structure expected by deserialiseScreenerData
  // The old screener expects an array where each index corresponds to a segment in the API response
  const subFilters: Array<{
    filter: { name: string };
    treeItems: Record<string, TreeItem>;
  }> = [];

  Object.entries(screenerFilters.segments || {}).forEach(
    ([metricKey, segments]) => {
      if (!Array.isArray(segments) || segments.length === 0) return;

      segments.forEach((segment) => {
        const segmentFilterEntries = Object.entries(segment.filter || {});
        const treeItems = buildTreeItems(segmentFilters, segmentFilterEntries);

        subFilters.push({
          filter: { name: metricKey },
          treeItems,
        });
      });
    }
  );

  return {
    filters: {
      sub_filters: subFilters,
    },
  };
};
